import React, { useState, useEffect, useRef } from 'react';
import SideMenuBar from '../../components/sideMenuBar';
import { useNavigate } from 'react-router-dom';
import { ACTIONS, KEYS, URLS } from '../../_config';
import { useDispatch } from 'react-redux';
import Icon from '../../components/icons';
import { useStoreSelector, useAuthStatus, useStoreBrnDSSelector } from '../../store/selectors/userSelector';
import { useReviewRatingApi } from '../../store/api/hooks/reviewratingApiHook';
import toast from 'react-hot-toast';
import Spinner from '../../components/iconSvgs/spinner';
import { CurrentRatingProps } from '../../_config/interfaces/interfaces';

const CurrentRating: React.FC<CurrentRatingProps> = ({ filterValue }) => {
    const [isOnSubmitLoading, setOnSubmitLoading] = useState<boolean>(false);
    const [ratingSummary, setRatingSummary] = useState<any>({});

    const [isTextVisible, setIsTextVisible] = useState(false);
    const iconRef = useRef<HTMLDivElement>(null);
    const textRef = useRef<HTMLDivElement>(null);

    const [isAvgTextVisible, setIsAvgTextVisible] = useState(false);
    const iconAvgRef = useRef<HTMLDivElement>(null);
    const textAvgRef = useRef<HTMLDivElement>(null);

    const [isSentiTextVisible, setIsSentiTextVisible] = useState(false);
    const iconSentiRef = useRef<HTMLDivElement>(null);
    const textSentiRef = useRef<HTMLDivElement>(null);

    const branchDS = useStoreBrnDSSelector();
    const reviewAPI = useReviewRatingApi();


    const handleIconClick = () => {
        setIsTextVisible(true);
    };


    const handleOutsideClick = (event: MouseEvent) => {
        if (
            iconRef.current && !iconRef.current.contains(event.target as Node) &&
            textRef.current && !textRef.current.contains(event.target as Node)
        ) {
            setIsTextVisible(false);
        }
    };

    const handleAvgIconClick = () => {
        setIsAvgTextVisible(true);
    };


    const handleAvgOutsideClick = (event: MouseEvent) => {
        if (
            iconAvgRef.current && !iconAvgRef.current.contains(event.target as Node) &&
            textAvgRef.current && !textAvgRef.current.contains(event.target as Node)
        ) {
            setIsAvgTextVisible(false);
        }
    };

    const handleSentiIconClick = () => {
        setIsSentiTextVisible(true);
    };


    const handleSentiOutsideClick = (event: MouseEvent) => {
        if (
            iconSentiRef.current && !iconSentiRef.current.contains(event.target as Node) &&
            textSentiRef.current && !textSentiRef.current.contains(event.target as Node)
        ) {
            setIsSentiTextVisible(false);
        }
    };

    useEffect(() => {
        document.addEventListener("click", handleOutsideClick);

        return () => {
            document.removeEventListener("click", handleOutsideClick);
        };
    }, []);

    useEffect(() => {
        document.addEventListener("click", handleAvgOutsideClick);

        return () => {
            document.removeEventListener("click", handleAvgOutsideClick);
        };
    }, []);

    useEffect(() => {
        document.addEventListener("click", handleSentiOutsideClick);

        return () => {
            document.removeEventListener("click", handleSentiOutsideClick);
        };
    }, []);

    useEffect(() => {
        if (filterValue !== null) {
            applyFilter(filterValue); // This is called automatically when filterValue changes
        } else {
            getRatingSummary({});
        }
    }, [filterValue]);

    const applyFilter = (filterValue: any) => {
        getRatingSummary(filterValue);
    }

    const getRatingSummary = (param: any) => {
        setOnSubmitLoading(true);
        let params: any;
        if (param) {
            params = { branch_id: branchDS?.branch_id, ...param };
        } else {
            params = { branch_id: branchDS?.branch_id };
        }
        try {
            setTimeout(() => {
                reviewAPI.getRatingSummary(params, (msg: any, resp: any) => {
                    setRatingSummary(resp.data);

                }, (err: any, resp: any) => {
                    toast.error(resp?.detail?.message);
                });
                setOnSubmitLoading(false);
            }, 3000);

        } catch (err: any) {
            toast.error(err);
            setOnSubmitLoading(false);
        }
    };

    const positive = ratingSummary.sentiment_breakdown?.positive ?? 0;
    const neutral = ratingSummary.sentiment_breakdown?.neutral ?? 0;
    const negative = ratingSummary.sentiment_breakdown?.negative ?? 0;
    const mixed = ratingSummary.sentiment_breakdown?.mixed ?? 0;

    let positiveWidth: any = 0;
    let negativeWidth: any = 0;
    let neutralWidth: any = 0;
    let mixedWidth: any = 0;

    const totalSentiment = positive + neutral + negative + mixed;

    if (totalSentiment > 0) {
        positiveWidth = (positive / totalSentiment) * 100;
        neutralWidth = (neutral / totalSentiment) * 100;
        negativeWidth = (negative / totalSentiment) * 100;
        mixedWidth = (mixed / totalSentiment) * 100;
    }

    const renderStars = (rating: any) => {
        const stars = [];
        for (let i = 0; i < 5; i++) {
            stars.push(
                <Icon
                    key={i}
                    name="full-star"
                    size={22}
                    className={`mr-1 ${i < Math.floor(rating) ? 'text-yellow-400' : 'text-gray-300'}`}
                />
            );
        }
        return stars;
    };

    return (
        <div className="flex flex-col">
            <h1 className="text-xl font-semibold text-gray-600 mb-4">Current Ratings </h1>

            {/* Paragraph */}
            {/* <p className="text-sm text-gray-600 mb-6">
                Not Matching The Public Store? You're Probably Looking For <span className='text-sm text-yellow-500 font-semibold'>Ratings</span>
            </p> */}
            <hr />

            {/* Main Content */}
            <div className="flex justify-between mobile-column">
                {isOnSubmitLoading && (
                    <div className="absolute inset-0 bg-gray-700 bg-opacity-50 flex justify-center items-center z-50">
                        <Spinner className="self-center z-50" />
                    </div>
                )}
                {/* Reviews Section */}
                <div className="w-1/2 p-4 border-r border-gray-200 flex flex-col relative mobile-width-100 mobile-border-0">
                    <div className='flex mb-4 items-center'>
                        <h1 className="text-sm font-semibold text-gray-600">Rating Count</h1>
                        <div ref={iconRef} className="pointer-events-auto">
                            <Icon name="info" size={20} className="text-gray-400" onClick={handleIconClick} />
                        </div>
                    </div>
                    {/* Text */}
                    {isTextVisible && (
                        <div
                            ref={textRef}
                            className="absolute top-11 bg-blue-200 w-32 h-32 overflow-y-auto p-2 z-50 rounded-md text-sm text-gray-700 hidden"
                        >
                            This is the text that appears when the icon is clicked.
                        </div>
                    )}
                    {/* <Icon name="info" size={20} className="absolute left-[105px] top-[26px] transform -translate-y-1/2 text-gray-400 pointer-events-auto" /> */}
                    <div className="flex flex-col items-center justify-center mt-6 relative">
                        <p className="bg-yellow-200 rounded-full w-32 h-32 flex items-center justify-center text-lg font-semibold text-gray-600 mb-6">
                            {ratingSummary?.total_rating_count}
                        </p>
                        <p className="text-sm font-semibold text-gray-400 mb-6">Total Ratings</p>
                    </div>
                </div>

                {/* Avg Stars Section */}
                <div className="w-1/2 p-4 border-r border-gray-200 relative mobile-width-100 mobile-border-0">
                    <div className='flex mb-4 items-center'>
                        <h1 className="text-sm font-semibold text-gray-600">Avg Stars</h1>
                        <div ref={iconAvgRef} className="pointer-events-auto">
                            <Icon name="info" size={20} className="text-gray-400" onClick={handleAvgIconClick} />
                        </div>
                    </div>

                    {/* Text */}
                    {isAvgTextVisible && (
                        <div
                            ref={textAvgRef}
                            className="absolute top-11 bg-blue-200 w-32 h-32 overflow-y-auto p-2 z-50 rounded-md text-sm text-gray-700 hidden"
                        >
                            This is the text that appears when the icon is clicked.
                        </div>
                    )}
                    {/* <Icon name="info" size={20} className="absolute left-[84px] top-[26px] transform -translate-y-1/2 text-gray-400 pointer-events-auto" /> */}
                    <div className="flex mb-2">
                        {renderStars(ratingSummary?.overall_avg_rating)}
                    </div>
                    <p className="text-lg font-semibold text-gray-600 mb-6">{ratingSummary?.overall_avg_rating ?? 0} Stars</p>
                </div>

                {/* Stars Breakdown Section */}
                <div className="w-1/2 p-4 relative mobile-width-100">
                    <div className='flex mb-4 items-center'>
                        <h1 className="text-sm font-semibold text-gray-600">Stars Breakdown</h1>
                        <div ref={iconSentiRef} className="pointer-events-auto">
                            <Icon name="info" size={20} className="text-gray-400" onClick={handleSentiIconClick} />
                        </div>
                    </div>
                    {/* Text */}
                    {isSentiTextVisible && (
                        <div
                            ref={textSentiRef}
                            className="absolute top-11 bg-blue-200 w-32 h-32 overflow-y-auto p-2 z-50 rounded-md text-sm text-gray-700 hidden"
                        >
                            This is the text that appears when the icon is clicked.
                        </div>
                    )}
                    {/* <Icon
                        name="info"
                        size={20}
                        className="absolute left-[135px] top-[26px] transform -translate-y-1/2 text-gray-400 pointer-events-auto"
                    /> */}
                    {/* 5 progress bars for 5 stars */}
                    {Array.from({ length: 5 }, (_, index) => {
                        let starRating;

                        switch (index) {
                            case 0:
                                starRating = ratingSummary?.stars_breakdown?.[5];
                                break;
                            case 1:
                                starRating = ratingSummary?.stars_breakdown?.[4];
                                break;
                            case 2:
                                starRating = ratingSummary?.stars_breakdown?.[3];
                                break;
                            case 3:
                                starRating = ratingSummary?.stars_breakdown?.[2];
                                break;
                            case 4:
                                starRating = ratingSummary?.stars_breakdown?.[1];
                                break;
                            default:
                                starRating = 0;
                                break;
                        }

                        const oneStars = ratingSummary?.stars_breakdown?.[1] ?? 0;
                        const twoStars = ratingSummary?.stars_breakdown?.[2] ?? 0;
                        const threeStars = ratingSummary?.stars_breakdown?.[3] ?? 0;
                        const fourStars = ratingSummary?.stars_breakdown?.[4] ?? 0;
                        const fiveStars = ratingSummary?.stars_breakdown?.[5] ?? 0;



                        const totalStars = oneStars + twoStars + threeStars + fourStars + fiveStars;

                        // const progress = (starRating / 5) * 100;
                        const progress = (starRating / totalStars) * 100;
                        const stars = Array.from({ length: 5 - index }, (_, i) => (
                            <Icon key={i} name="full-star" size={20} className="text-gray-300" />
                        ));

                        return (
                            <div key={index} className="flex flex-col items-center mb-4 w-full">
                                <div className="w-full bg-gray-300 rounded-full h-2 mb-2 overflow-hidden">
                                    <div
                                        className="h-2 bg-yellow-500 rounded-l-full"
                                        style={{ width: `${progress}%` }}
                                    ></div>
                                </div>

                                <div className="flex justify-between w-full">
                                    <div className="flex">
                                        {stars}
                                    </div>
                                    {/* <span className="text-yellow-500">
                                        <Icon name="full-star" size={22} className="text-gray-300" />
                                    </span> */}

                                    <span className="text-sm font-semibold text-gray-600">{starRating}</span>
                                </div>

                            </div>
                        );
                    })}
                </div>


            </div>
        </div>
    );
}


export default CurrentRating;